/**
 * 渲染桥（M2-04 / DESIGN.md §4.1）。
 *
 * CanvasView（UI 层）不认识 TileStore；它只报告「现在的视口是什么」。
 * 本模块站在应用层，把文档合成结果画到它给的 canvas 上：
 *
 *   store 变化 → CompositeCache 标脏 → 每帧 update()（只重算脏块）
 *   → 离屏 canvas → 按视口变换 drawImage
 *
 * 变换顺序必须与 interaction/viewport.ts 的 documentToScreen 完全一致
 * （T·R·S：translate → rotate → scale），否则拾色、画笔落点会整体偏移。
 *
 * 脏粒度现状：结构性命令（改不透明度、增删图层）标整文档脏；
 * 像素级的按块标脏在画笔任务（M2-11）里经 markTileDirty 接入。
 */
import type { Size, Viewport } from './viewport';
import { documentBoundsOnScreen, documentViewportRect } from './viewport';
import { CompositeCache } from '../app/composite-cache';
import type { AppStore } from '../app/store';
import type { GpuCompositor } from '../app/gpu/compositor';
import { isolateChannel, type ChannelIsolation } from '../engine/color/isolate-channel';

/** 棋盘格方块边长（CSS 像素）。跟 CanvasView.tsx 占位绘制那份一致，两边各自
 * 维护是因为 UI 层不能被交互层反向 import（CLAUDE.md 规则 4）。 */
const CHECKER_CELL = 8;

/** 棋盘格图案，按颜色缓存——同 CanvasView.tsx 的占位绘制，见那边的注释。 */
const checkerPatternCache = new Map<string, CanvasPattern | null>();

function checkerPattern(ctx: CanvasRenderingContext2D, colorA: string, colorB: string): CanvasPattern | null {
  const key = `${colorA}|${colorB}`;
  const cached = checkerPatternCache.get(key);
  if (cached !== undefined) return cached;

  const tile = document.createElement('canvas');
  tile.width = CHECKER_CELL * 2;
  tile.height = CHECKER_CELL * 2;
  const tileCtx = tile.getContext('2d');
  let pattern: CanvasPattern | null = null;
  if (tileCtx !== null) {
    tileCtx.fillStyle = colorA;
    tileCtx.fillRect(0, 0, CHECKER_CELL * 2, CHECKER_CELL * 2);
    tileCtx.fillStyle = colorB;
    tileCtx.fillRect(CHECKER_CELL, 0, CHECKER_CELL, CHECKER_CELL);
    tileCtx.fillRect(0, CHECKER_CELL, CHECKER_CELL, CHECKER_CELL);
    pattern = ctx.createPattern(tile, 'repeat');
  }
  checkerPatternCache.set(key, pattern);
  return pattern;
}

export interface RenderBridgeOptions {
  /** GPU 合成器（M3-09/10）。null = 没有可用后端，恒走 CPU 合成桥。 */
  readonly gpu?: GpuCompositor | null;
  /** GPU 帧就绪后触发重绘（跨一帧的异步结果，需要主动请求下一次渲染）。 */
  readonly requestRender?: () => void;
}

export interface RenderBridge {
  /** 交给 CanvasView 的渲染回调。返回 false 表示没有文档，退回占位绘制。 */
  readonly onRender: (canvas: HTMLCanvasElement, viewport: Viewport, view: Size) => boolean;
  /** 像素级标脏入口（画笔等像素操作用）。 */
  readonly markTileDirty: (tx: number, ty: number) => void;
  /** 累计重算块数。e2e 靠它断言「没变就不重算」。 */
  readonly compositedTiles: () => number;
  /** 最近一次成功的 GPU 帧耗时（ms）；没有 GPU 后端或还没有过成功结果时为 null。
   * 性能面板（M5-01）读它跟 §8 的视口预算比对。 */
  readonly lastGpuSubmitMs: () => number | null;
  /** 叠加层（蚂蚁线等）。在文档像素之上、以 CSS 像素坐标系绘制。 */
  readonly setOverlay: (draw: ((ctx: CanvasRenderingContext2D) => void) | null) => void;
  /** 文档空间叠加层（画笔预览等）。在视口变换**内**绘制，坐标即文档像素。 */
  readonly setDocOverlay: (draw: ((ctx: CanvasRenderingContext2D) => void) | null) => void;
  /** 显示期隐藏某个图层（自由变换会话用；预览由 doc-overlay 承担）。 */
  readonly setHiddenLayer: (id: string | null) => void;
  /** 通道面板"只看这个通道"（DESIGN.md §7.1 补充）。null＝正常 RGB 视图。 */
  readonly setChannelIsolation: (channel: ChannelIsolation | null) => void;
  /** 最近一次渲染的视口与视图尺寸，供叠加层的动画节拍使用。 */
  readonly lastViewport: () => Viewport | null;
  readonly lastView: () => Size | null;
}

export function createRenderBridge(store: AppStore, options: RenderBridgeOptions = {}): RenderBridge {
  const gpu = options.gpu ?? null;
  const requestRender = options.requestRender ?? ((): void => {});

  let cache: CompositeCache | null = null;
  let cachedDocumentId: string | null = null;
  let lastDocument = store.displayDocument();
  let offscreen: HTMLCanvasElement | null = null;
  let imageData: ImageData | null = null;
  let totalComposited = 0;
  let overlay: ((ctx: CanvasRenderingContext2D) => void) | null = null;
  let docOverlay: ((ctx: CanvasRenderingContext2D) => void) | null = null;
  let lastViewport: Viewport | null = null;
  let lastView: Size | null = null;

  // GPU 一帧滞后的显示缓存（与 AntsOverlay 同一纪律：GPU 渲染是异步的，
  // onRender 必须同步返回，只能"这一帧画上一次算好的结果，同时另起一次
  // 请求供下一帧用"）。rect 记录这份像素对应的文档空间矩形，用来判断
  // 它是否还覆盖得住当前视口——旧结果覆盖不住就退回 CPU 合成桥。
  let gpuFrame: {
    rect: { x: number; y: number; width: number; height: number };
    pixels: Uint8Array;
    width: number;
    height: number;
    /**
     * 这份结果是照着哪个文档对象算出来的。结构性 dispatch 会换一个新的
     * Document 引用，但**纯像素操作（画笔等）不会**——那条路径只改
     * TileStore，压根不碰 Document 对象（像素归 TileStore 管，规则 6）。
     * 所以引用比对只能抓到结构性变化，像素级变化还得配合下面的 epoch。
     */
    forDocument: NonNullable<ReturnType<typeof store.displayDocument>>;
    /** 请求那一刻的 pixelEpoch，抓纯像素改动（结构引用不变的那种）。 */
    forPixelEpoch: number;
    /**
     * 请求那一刻的 hiddenLayer（自由变换/移动会话隐藏原图层用）。
     *
     * 文档引用与 pixelEpoch 都不认这个变化：`setHiddenLayer` 只是
     * render-bridge 自己的本地状态，不经过 store，既不换文档引用也不碰
     * pixelEpoch。真实出过的 bug：变换提交时"先烘焙+invalidatePixels
     * （epoch+1），再 setHiddenLayer(null)"，epoch+1 那一刻发出的 GPU 请求
     * 用的还是"隐藏原图层"的旧状态，等它异步返回、epoch 也对上了，
     * 会被误判成"新鲜"直接拿来用——画面停在图层还被隐藏那一帧，
     * 明明 CPU 合成桥早就是对的。
     */
    forHiddenLayer: string | null;
  } | null = null;
  let gpuInFlight = false;
  let gpuOffscreen: HTMLCanvasElement | null = null;
  let gpuImageData: ImageData | null = null;
  /** 最近一次 GPU 帧从发起请求到拿到结果的耗时（ms）——M5-01 性能面板用，
   * 跟"视口平移/缩放"预算（§8）对齐。请求失败/还没有过一次成功结果时是 null。 */
  let gpuLastSubmitMs: number | null = null;

  const rectCovers = (
    rect: { x: number; y: number; width: number; height: number },
    need: { x: number; y: number; width: number; height: number },
  ): boolean =>
    rect.x <= need.x &&
    rect.y <= need.y &&
    rect.x + rect.width >= need.x + need.width &&
    rect.y + rect.height >= need.y + need.height;

  /** 异步拿一帧 GPU 结果；不阻塞 onRender，就绪后自己触发下一次渲染。 */
  const requestGpuFrame = (
    doc: NonNullable<ReturnType<typeof store.displayDocument>>,
    rect: { x: number; y: number; width: number; height: number },
  ): void => {
    if (gpu === null || gpuInFlight || rect.width <= 0 || rect.height <= 0) return;
    gpuInFlight = true;
    const requestedEpoch = store.pixelEpoch;
    const requestedHiddenLayer = hiddenLayer;
    const submitStart = performance.now();
    void gpu
      .renderViewport(doc, store.tiles, rect, {
        ...(requestedHiddenLayer === null ? {} : { skipLayerId: requestedHiddenLayer }),
      })
      .then((frame) => {
        if (frame === null) return; // 文档含 GPU 路径未覆盖的内容（ADR-0005）：保持 CPU 结果
        gpuLastSubmitMs = performance.now() - submitStart;
        gpuFrame = {
          rect,
          pixels: frame.pixels,
          width: frame.width,
          height: frame.height,
          forDocument: doc,
          forPixelEpoch: requestedEpoch,
          forHiddenLayer: requestedHiddenLayer,
        };
        requestRender();
      })
      .catch(() => {
        // GPU 渲染失败（含上下文不可恢复）：保留上一份可用的结果或 null，
        // 两种情况 CPU 合成桥都能兜底，不需要在这里特殊处理
      })
      .finally(() => {
        gpuInFlight = false;
      });
  };

  /**
   * 文档变化检测放在渲染路径内、而不是订阅回调里：订阅回调与 UI 重绘的
   * 相对顺序取决于注册次序，靠它会出现「先画后标脏」的竞态——
   * 画面停留在旧内容直到下一次视口变化。渲染前自查则与顺序无关。
   */
  let lastPixelEpoch = store.pixelEpoch;
  let hiddenLayer: string | null = null;
  let channelIsolation: ChannelIsolation | null = null;
  // CPU 离屏画布走缓存（只有脏块变化才重新 putImageData），单独记一份
  // "上次实际画上去的是哪个通道"，通道隔离切换但像素没变时也要强制重画一次
  let lastAppliedChannelIsolation: ChannelIsolation | null = null;

  const syncWithDocument = (): void => {
    // 像素级失效（画笔、像素 undo）：文档引用不变，但内容变了。
    // GPU tile 来源也要失效，否则已经上传过的块纹理会一直是改动前的内容——
    // 这是 GpuCompositor.invalidateTiles 存在的唯一理由
    if (store.pixelEpoch !== lastPixelEpoch) {
      lastPixelEpoch = store.pixelEpoch;
      for (const { tx, ty } of store.pixelDirty) cache?.markTileDirty(tx, ty);
      gpu?.invalidateTiles();
    }
    const document = store.displayDocument();
    if (document === lastDocument) return; // hint 等无关变化
    lastDocument = document;
    if (document === null) {
      cache = null;
      cachedDocumentId = null;
      return;
    }
    if (
      cache === null ||
      cachedDocumentId !== document.id ||
      cache.width !== document.size.width ||
      cache.height !== document.size.height
    ) {
      cache = new CompositeCache(document.size.width, document.size.height);
      cachedDocumentId = document.id;
      offscreen = null; // 尺寸变了，离屏画布重建
    } else {
      // 结构性变化（可见性/不透明度/增删图层）影响面未知，整体标脏。
      // 像素操作走 markTileDirty 的精确路径，不经过这里
      cache.markAllDirty();
    }
  };

  const ensureOffscreen = (width: number, height: number): CanvasRenderingContext2D | null => {
    if (offscreen === null || offscreen.width !== width || offscreen.height !== height) {
      offscreen = document.createElement('canvas');
      offscreen.width = width;
      offscreen.height = height;
      imageData = null;
    }
    return offscreen.getContext('2d');
  };

  const onRender = (canvas: HTMLCanvasElement, viewport: Viewport, view: Size): boolean => {
    try {
      return renderFrame(canvas, viewport, view);
    } catch (error) {
      // 合成/绘制阶段一旦抛错，没有这层 try/catch 的话 effect 会直接
      // 中断，画布停在占位棋盘格上再也不会重绘，且没有任何提示——
      // 退回占位并把错误亮出来，好过一份看起来"什么都没发生"的死画布
      console.error('渲染帧失败', error);
      store.setHint(`渲染失败：${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  };

  const renderFrame = (canvas: HTMLCanvasElement, viewport: Viewport, view: Size): boolean => {
    syncWithDocument();
    // 显示路径读 displayDocument：调整参数拖动预览不进文档就能上屏（M3-06）
    const doc = store.displayDocument();
    if (doc === null || cache === null) return false;

    const updated = cache.update(doc, store.tiles, hiddenLayer ?? undefined);
    totalComposited += updated;

    const offCtx = ensureOffscreen(cache.width, cache.height);
    const ctx = canvas.getContext('2d');
    if (offCtx === null || ctx === null) return false;

    if (updated > 0 || imageData === null || channelIsolation !== lastAppliedChannelIsolation) {
      if (imageData === null) imageData = new ImageData(cache.width, cache.height);
      imageData.data.set(cache.pixels);
      if (channelIsolation !== null) isolateChannel(imageData.data, channelIsolation);
      offCtx.putImageData(imageData, 0, 0);
      lastAppliedChannelIsolation = channelIsolation;
    }

    // GPU 路径（M3-10）：已有帧若覆盖不住当前视口矩形，或不是照当前这个
    // 文档对象算出来的（任意一次 dispatch，哪怕只是结构性的，都会换一个新
    // 引用），就异步再取一帧——本帧仍然用 CPU 合成桥的结果兜底，
    // GPU 帧就绪是下一帧的事（跟 AntsOverlay 同纪律）
    const neededRect = documentViewportRect(doc.size, viewport, view);
    const gpuFrameStale =
      gpuFrame === null ||
      gpuFrame.forDocument !== doc ||
      gpuFrame.forPixelEpoch !== store.pixelEpoch ||
      gpuFrame.forHiddenLayer !== hiddenLayer ||
      !rectCovers(gpuFrame.rect, neededRect);
    if (gpu !== null && gpuFrameStale) requestGpuFrame(doc, neededRect);

    const dpr = window.devicePixelRatio || 1;
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // 底色与文档内的透明棋盘格从 token 读（§7.2）
    const style = getComputedStyle(canvas);
    const token = (name: string, fallback: string): string => {
      const value = style.getPropertyValue(name).trim();
      return value === '' ? fallback : value;
    };
    ctx.fillStyle = token('--color-canvas-bg', 'gray');
    ctx.fillRect(0, 0, view.width, view.height);

    // 文档范围内画真正的棋盘格表示透明——之前这里只拿 checker-a 一个颜色铺满，
    // 画出来是一片纯色而不是棋盘格，透明区域（比如 Delete 挖穿的洞）看着像
    // 不透明灰底；跟 CanvasView.tsx 的占位绘制用同一套 pattern 手法。
    const bounds = documentBoundsOnScreen(doc.size, viewport);
    const checkerA = token('--color-checker-a', 'white');
    const checkerB = token('--color-checker-b', 'silver');
    const pattern = checkerPattern(ctx, checkerA, checkerB);
    ctx.save();
    ctx.beginPath();
    ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height);
    ctx.clip();
    if (pattern !== null) {
      ctx.fillStyle = pattern;
      ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
    } else {
      ctx.fillStyle = checkerA;
      ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
    }
    ctx.restore();

    // 与 documentToScreen 一致的 T·R·S
    ctx.translate(viewport.tx, viewport.ty);
    ctx.rotate(viewport.rotation);
    ctx.scale(viewport.scale, viewport.scale);
    // 放大看像素时用最近邻（行业惯例），缩小时用平滑避免闪烁锯齿
    ctx.imageSmoothingEnabled = viewport.scale < 2;

    // 一份足够新鲜的 GPU 帧才用来画：照当前文档对象算出来（结构没变）、
    // 当次请求时的像素状态与现在一致（画笔这类不换文档引用的改动也要抓到，
    // 像素归 TileStore 管、不在 Document 对象上，引用比对单独抓不住它）、
    // 且覆盖当前视口。不满足就画 CPU 合成桥的整文档离屏画布（永远可用的
    // 兜底）——比把过时画面当作"最新"画出来更安全，新 GPU 帧下一帧才会替换它
    const usableGpuFrame =
      gpuFrame !== null &&
      gpuFrame.forDocument === doc &&
      gpuFrame.forPixelEpoch === store.pixelEpoch &&
      gpuFrame.forHiddenLayer === hiddenLayer &&
      rectCovers(gpuFrame.rect, neededRect)
        ? gpuFrame
        : null;
    if (usableGpuFrame !== null) {
      if (
        gpuOffscreen === null ||
        gpuOffscreen.width !== usableGpuFrame.width ||
        gpuOffscreen.height !== usableGpuFrame.height
      ) {
        gpuOffscreen = document.createElement('canvas');
        gpuOffscreen.width = usableGpuFrame.width;
        gpuOffscreen.height = usableGpuFrame.height;
        gpuImageData = null;
      }
      const gpuCtx = gpuOffscreen.getContext('2d');
      if (gpuCtx !== null) {
        if (gpuImageData === null) {
          gpuImageData = new ImageData(usableGpuFrame.width, usableGpuFrame.height);
        }
        gpuImageData.data.set(usableGpuFrame.pixels);
        if (channelIsolation !== null) isolateChannel(gpuImageData.data, channelIsolation);
        gpuCtx.putImageData(gpuImageData, 0, 0);
        ctx.drawImage(gpuOffscreen, usableGpuFrame.rect.x, usableGpuFrame.rect.y);
      }
    } else {
      ctx.drawImage(offscreen!, 0, 0);
    }
    docOverlay?.(ctx); // 仍在视口变换内：文档空间
    ctx.restore();

    // 叠加层在视口变换之外、CSS 像素坐标系里绘制（蚂蚁线已在着色器里做过变换）
    if (overlay !== null) {
      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      overlay(ctx);
      ctx.restore();
    }
    lastViewport = viewport;
    lastView = view;

    // e2e 探针：与 data-viewport 同一风格，挂在容器的 data 属性上
    canvas.parentElement?.setAttribute('data-composited-tiles', String(totalComposited));
    return true;
  };

  return {
    onRender,
    markTileDirty: (tx, ty) => cache?.markTileDirty(tx, ty),
    compositedTiles: () => totalComposited,
    lastGpuSubmitMs: () => gpuLastSubmitMs,
    setOverlay: (draw) => {
      overlay = draw;
    },
    setDocOverlay: (draw) => {
      docOverlay = draw;
    },
    setHiddenLayer: (id) => {
      if (hiddenLayer === id) return;
      hiddenLayer = id;
      cache?.markAllDirty();
    },
    setChannelIsolation: (channel) => {
      if (channelIsolation === channel) return;
      channelIsolation = channel;
      requestRender();
    },
    lastViewport: () => lastViewport,
    lastView: () => lastView,
  };
}
